import http from "../http-common";
const getAllActiveCustomers = () => {
  return http.get('/customers/active');
};

/**
 * Search/paginate customers eligible for medical appointments (active; not discharged/transferred/deceased).
 * Without `page`, returns a plain array. With `page`, returns `{ items, total, page, pageSize, totalPages }`.
 */
const listMedicalAppointmentCustomers = ({ page, pageSize, keyword, fields } = {}) => {
  const params = {};
  if (page != null && page !== '') params.page = page;
  if (pageSize != null && pageSize !== '') params.pageSize = pageSize;
  if (keyword != null && `${keyword}`.trim() !== '') params.keyword = `${keyword}`.trim();
  if (fields != null && `${fields}`.trim() !== '') params.fields = `${fields}`.trim();
  return http.get('/customers/active', { params });
};

const getAllCustomers = () => {
  return http.get('/customers');
};

/** Lighter customer payload for medical multi-day list (fewer fields, less memory). */
const getCustomersForMedicalList = () => {
  return http.get('/customers', { params: { fields: 'medical_list' } });
};

const exportAdmissionDischargeLog = () => {
  return http.get("/customers/export-admission-discharge-log", {
    responseType: "blob",
  });
};

const exportVitalSignsChart = () => {
  return http.get("/customers/export-vital-signs-chart", {
    responseType: "blob",
  });
};

const createNewCustomer = (data) => {
  data.status = 'active';
	return http.post('/customers', data);
};

const createNewClient = (data) => {
  data.status = 'active';
  return http.post('/clients', data);
}

const getCustomerChangeHistory = (id) => {
  return http.get(`/customers/${id}/change-history`);
};

const updateCustomer = (id, data) => {
  return http.put(`/customers/${id}`, data);
};

const bulkUpdateCustomers = (data) => {
  return http.put("/customers/bulk", data);
};

const updateClient = (id, data) => {
  data.status = 'active';
  return http.put(`/clients/${id}`, data);
}

const deleteCustomer = (id, data) => {
  data.status = 'inactive';
  return http.put(`/customers/${id}`, data);
}

/** Hard delete from DB; server enforces testadmin03-only. */
const permanentlyDeleteCustomer = (id) => {
  return http.delete(`/customers/${id}`);
};

const deleteClient = (id, data) => {
  data.status = 'inactive';
  return http.put(`/clients/${id}`, data);
}

const uploadDriverSignature = (filename, data, options = {}) => {
  const safeFilename = `${filename || ''}`.trim();
  if (!safeFilename || safeFilename.includes('undefined') || safeFilename.includes('null')) {
    throw new Error('Invalid upload filename for avatar/signature.');
  }
  return http.post(`/files/upload/${encodeURIComponent(safeFilename)}`, data, {
    params: options
  })
}

const uploadAvatar = (filename, data, requestConfig = {}) => {
  const safeFilename = `${filename || ''}`.trim();
  if (!safeFilename || safeFilename.includes('undefined') || safeFilename.includes('null')) {
    throw new Error('Invalid upload filename for avatar/signature.');
  }
  return http.post(`/files/upload-general/${encodeURIComponent(safeFilename)}`, data, requestConfig);
}

const getAvatar = (filename) => {
  return http.get(`/files/${filename}`);
}

const getAvatarAsBlob = (filename) => {
  return http.get(`/files/${filename}`, { responseType: 'blob' });
}

const deleteFile = (data) => {
  return http.post(`/files/delete`, data);
}

const getCustomersByNameOrEmail = (nameOrEmail) => {
  return http.get(`/customers/search`, {params: {nameOrEmail}});
}

const getClientsByNameOrEmail = (nameOrEmail) => {
  return http.get(`/clients/search`, {params: {nameOrEmail}});
}

const getCustomer = (id) => {
  return http.get(`customers/${id}`);
}

const getClient = (id) => {
  return http.get(`clients/${id}`);
}

const uploadCustomerFile = (data, customerId, name, fileType) => {
  return http.post(`/files/upload-physical?objectId=${customerId}&name=${name}&fileType=${fileType}&model=customer`, data);
}

const getAllCustomerFiles = (customerId, name, fileType) => {
  return http.get(`/files/list?objectId=${customerId}&name=${name}&fileType=${fileType}&model=customer`);
}

const getCustomerFormFiles = (customerId, customerName, fileType) => {
  return http.get(`/files/uploadedDocs/customer/${customerId}/type/${fileType}/name/${encodeURIComponent(customerName)}`);
}

const getFileDownloadUrl = (fileUrl) => {
  const baseUrl = http.defaults.baseURL || '';
  return baseUrl.replace('/api', '') + fileUrl;
}

/** Turn `/files/...` from the API into an absolute URL for `<img src>`. */
const resolveCustomerAvatarImageUrl = (fileUrl) => {
  if (!fileUrl) return null;
  if (fileUrl.startsWith('data:') || fileUrl.startsWith('http')) return fileUrl;
  const base = (http.defaults.baseURL || '').replace(/\/api$/, '');
  const pathPart = fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
  return `${base}${pathPart}`;
};

/** Resolve a customer avatar to a JPG URL served from `/files/*`. */
const getCustomerAvatarUrl = (customerId, { bypassCache = false } = {}) => {
  const params = { id: customerId };
  if (bypassCache) params._ = Date.now();
  return http.get('/customers/avatar', { params });
};

export { resolveCustomerAvatarImageUrl };

export const CustomerService = {
  getAllActiveCustomers,
  listMedicalAppointmentCustomers,
  uploadAvatar,
  uploadDriverSignature,
  getAvatar,
  getAvatarAsBlob,
  deleteFile,
  createNewCustomer,
  updateCustomer,
  bulkUpdateCustomers,
  getCustomerChangeHistory,
  deleteCustomer,
  permanentlyDeleteCustomer,
  getCustomersByNameOrEmail,
  getCustomer,
  getAllCustomers,
  getCustomersForMedicalList,
  exportAdmissionDischargeLog,
  exportVitalSignsChart,
  createNewClient,
  updateClient,
  deleteClient,
  getClientsByNameOrEmail,
  getClient,
  uploadCustomerFile,
  getAllCustomerFiles,
  getCustomerFormFiles,
  getFileDownloadUrl,
  getCustomerAvatarUrl,
  resolveCustomerAvatarImageUrl,
};
